import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./context/authContext";
import * as Sentry from "@sentry/react";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import "react-toastify/dist/ReactToastify.css";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  ThemeContextProvider,
  useThemeContext,
} from "./context/themeContext.js";
import {
  createTheme,
  CssBaseline,
  GlobalStyles,
  responsiveFontSizes,
  ThemeProvider,
} from "@mui/material";
import AppRoutes from "./routes/AppRoutes.js";
import RouteErrorBoundary from "./routes/RouteErrorBoundary.js";
import { handleApiError } from "./utils/utils.js";
import DialogTransition from "./components/DialogTransition.js";

if (process.env.NODE_ENV === "production") {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    // Tags every event with the app version (baked in by the build script) so
    // issues can be tied to a release and regressions attributed to one.
    release: process.env.REACT_APP_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Sentry.replayIntegration(), // requires cookie consent
    ],
    tracesSampleRate: 0.2,
    tracePropagationTargets: ["https://curateapp.io"],
    // replaysSessionSampleRate: 0.1, // requires cookie consent
    // replaysOnErrorSampleRate: 1.0, // requires cookie consent
  });
}

const queryClient = new QueryClient({
  // QueryCache handles global query error toasting — onError was removed from
  // defaultOptions.queries in TanStack Query v5.
  queryCache: new QueryCache({
    onError: handleApiError,
  }),
  defaultOptions: {
    queries: {
      // Global query defaults:
      // We disable all automatic refetching and treat all cached data as always-fresh.
      // This ensures:
      //   • Infinite queries don't refetch unexpectedly (e.g. on window focus/reconnect).
      //   • Lists and paginated data remain stable unless we explicitly update/refresh them.
      //   • UI state is fully controlled and predictable, especially when combined with manual
      //     cache updates after mutations (e.g. reordering lists, toggling statuses).
      //
      // IMPORTANT:
      // Using these defaults means any mutation that affects query data must trigger a manual
      // cache update or an explicit invalidateQuery(). React Query will not do this for us.
      // This pattern is preferred here because our data is highly interactive (lists, statuses,
      // pagination) and automatic refetches would cause jitter, flicker, and broken pagination.
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true, // Required so queries invalidated while unmounted will refetch when the component mounts again
      // e.g. to ensure track page queries (infJournal/infStatuses) are invalidated when changing status from explore page

      // Attempt requests even when the browser reports offline (navigator.onLine === false).
      // React Query's default ("online") *pauses* fetches when offline, so they never error —
      // meaning no network error reaches handleApiError/throwOnError and pages hang blank
      // instead of showing the Offline screen. "always" lets the request fail for real so the
      // existing retry/throwOnError/onError handling below can run.
      networkMode: "always",

      // Throw server errors (4xx/5xx) so the QueryErrorBoundary catches them.
      // Two intentional exclusions:
      //   • Network errors (!error.response) — show a toast and let the page render
      //     in a degraded state rather than blowing up the UI.
      //   • The dead-session 401 — useApi's refresh/redirect flow has already
      //     handled it (clean re-login) and flags it `sessionExpired`, so it must
      //     not flash the error boundary during the redirect. Any other error,
      //     including a non-auth 401, still throws.
      throwOnError: (error) => !!error.response && !error.sessionExpired,
      retry: (failureCount, error) => {
        // No connection — retrying is pointless until the user is back online
        if (!error.response) return false;

        // Definitive errors — no retry
        if (error.response.data?.isUserVisible) return false;
        if (error.response.status !== 503) return false;

        // 503 Service Unavailable — retry up to 2 times for transient server issues
        return failureCount < 2;
      },
    },
    mutations: { onError: handleApiError, retry: false, networkMode: "always" },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

function Root() {
  const { mode, isTouchDevice } = useThemeContext();
  let theme = createTheme({
    palette: {
      mode,
      primary: {
        main: "#00d37e",
        contrastText: "#fff",
      },
      secondary: {
        main: mode === "dark" ? "#1D3639" : "#f0f0f0",
        contrastText: mode === "dark" ? "#fff" : "#122425",
      },
      tertiary: {
        main: "#b1a7ec",
        contrastText: "#fff",
      },
      background: {
        default: mode === "dark" ? "#122425" : "#fff",
        paper: mode === "dark" ? "#122425" : "#fff",
      },
      text: {
        primary: mode === "dark" ? "#fff" : "#122425",
        // secondary: "#555555", // Secondary text color
      },
      status: {
        queued: {
          main: "#b1a7ec",
          contrastText: "#fff",
        },
        started: {
          main: "#9ee7b9",
          contrastText: "#333",
        },
        abandoned: {
          main: "#f27c75",
          contrastText: "#fff",
        },
        finished: {
          main: "#00d37e",
          contrastText: "#fff",
        },
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none", // Remove capitalisation
            boxShadow: "none",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          root: {
            maxWidth: "none", // remove the forced constraint entirely
            whiteSpace: "nowrap", // prevent wrapping on small screens
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          // Fade + subtle rise on open/close, applied globally so every dialog
          // (StandardDialog, LibraryModal, ad-hoc ones) inherits it automatically.
          TransitionComponent: DialogTransition,
          transitionDuration: { enter: 240, exit: 180 },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          // Shifts every fixed AppBar down by the safe-area top inset plus the campaign
          // banner's height (composed into --top-offset in GlobalStyles above). Lives
          // here rather than in GlobalStyles because a `.MuiAppBar-root` global rule ties
          // on specificity with MUI's own `top: 0` and loses whenever an AppBar's styles
          // are injected later.
          // Defaults to 0px, so it's a no-op whenever there's no inset and no campaign.
          positionFixed: {
            top: "var(--top-offset, 0px)",
          },
        },
      },
    },
  });

  theme = responsiveFontSizes(theme);

  const inputGlobalStyles = (
    <GlobalStyles
      styles={{
        img: {
          WebkitTouchCallout: "none", // prevents image callout on iOS — Android is handled via contextmenu event listener in Spa.js
          WebkitUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
          userSelect: "none",
        },
        ":root": {
          // Safe-area insets (notch/status-bar/home-indicator). Resolve to 0px anywhere
          // there's nothing to inset — desktop, browser tabs, non-notched phones — since
          // env() only returns non-zero values when index.html's viewport-fit=cover is
          // active AND the device has a real inset. So every consumer below is a no-op
          // outside the installed-PWA/notched case, with no platform branching needed.
          "--safe-top": "env(safe-area-inset-top, 0px)",
          "--safe-bottom": "env(safe-area-inset-bottom, 0px)",
          "--safe-left": "env(safe-area-inset-left, 0px)",
          "--safe-right": "env(safe-area-inset-right, 0px)",
          // Combines the top inset with the existing campaign-banner offset so every
          // top-anchored element (AppBars, drawers, RootLayout) can read one variable
          // instead of composing the two itself.
          "--top-offset":
            "calc(var(--safe-top) + var(--campaign-banner-height, 0px))",

          // Toast colours — inverted surface (page text colour as background and vice
          // versa) so toasts stand out in both modes without clashing with the
          // status colours; the type colour shows via the icon. In light mode that's
          // #122425, the darkest colour in the dark-mode palette.
          "--toastify-color-light": theme.palette.text.primary,
          "--toastify-color-dark": theme.palette.text.primary,
          "--toastify-text-color-light": theme.palette.background.default,
          "--toastify-text-color-dark": theme.palette.background.default,
          "--toastify-color-success": theme.palette.primary.main,
          "--toastify-color-warning": theme.palette.tertiary.main,
          "--toastify-color-error": theme.palette.status.abandoned.main,
          "--toastify-color-info": theme.palette.tertiary.main,
          "--toastify-color-progress-light": theme.palette.primary.main,
          "--toastify-color-progress-dark": theme.palette.primary.main,

          // Toast layout styles
          "--toastify-toast-bd-radius": `${theme.shape.borderRadius}px`,
          "--toastify-toast-shadow": "0px 4px 12px rgba(0, 0, 0, 0.2)",
          "--toastify-font-family": theme.typography.fontFamily,

          // Toast margin/padding
          "--toastify-toast-padding": "8px",
          "--toastify-toast-min-height": "0px",
        },
        // Toastify colours the close button for its own theme surface (white in dark
        // mode), which would vanish on the inverted background — match the text.
        ".Toastify__close-button": {
          color: `var(--toastify-text-color-${theme.palette.mode})`,
          opacity: 0.5,
        },
        "@media only screen and (max-width: 480px)": {
          // Doubled class out-ranks toastify's own mobile rules (left: 0, bottom: 0).
          ".Toastify__toast-container.Toastify__toast-container--bottom-center": {
            width: "calc(100% - 32px)", // full width minus gutters, so long messages wrap less
            left: "50%",
            right: "auto",
            transform: "translateX(-50%)",
            // Whichever reaches higher: the open keyboard (see RootLayout), or the top
            // of the FAB (60px tall, max(30px, safe-bottom) up — see SearchButton).
            // Each toast's 10px marginBottom supplies the gap above either.
            bottom:
              "max(var(--keyboard-inset, 0px), max(30px, var(--safe-bottom)) + 60px)",
            transition: "bottom 150ms ease-out",
          },
          // While a dialog is open the FAB isn't usable, so toasts drop its clearance and
          // just clear the keyboard, or sit a short way off the bottom edge (or above the
          // home indicator, if that's taller). MuiModal-hidden marks keepMounted dialogs
          // that are closed, which shouldn't count.
          "body:has(.MuiDialog-root:not(.MuiModal-hidden)) .Toastify__toast-container.Toastify__toast-container--bottom-center":
            {
              bottom:
                "max(var(--keyboard-inset, 0px), max(24px, var(--safe-bottom)))",
            },
          ".Toastify__toast": {
            borderRadius: `${theme.shape.borderRadius}px`, // override default of zero
            marginBottom: "10px", // ensures gap between multiple toasts
            // Material snackbar proportions: 48px single line, roomier padding
            minHeight: "48px",
            padding: "10px 14px",
            fontSize: theme.typography.body2.fontSize,
            lineHeight: 1.5,
          },
          ".Toastify__toast-body": {
            padding: 0, // toast padding alone sets the height
          },
          ".Toastify__toast-icon": {
            width: "20px",
            marginInlineEnd: "12px",
          },
        },
        ".MuiSpeedDialAction-staticTooltipLabel": {
          // styles applied to the static tooltip label if tooltipOpen={true}
          whiteSpace: "nowrap",
        },
        /* Recharts styles */
        ".recharts-wrapper": {
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
        },
        ".recharts-cartesian-axis-tick text": {
          fill: theme.palette.text.secondary,
          fontSize: "12px",
        },
        ".recharts-cartesian-grid line": {
          stroke: theme.palette.divider,
        },
        ".recharts-default-tooltip": {
          backgroundColor: `${theme.palette.background.paper} !important`,
          border: "none !important",
          borderRadius: `${theme.shape.borderRadius}px !important`,
          boxShadow: "0px 4px 12px rgba(0,0,0,0.2) !important",
          padding: "8px 10px !important",
        },
        ".recharts-tooltip-label": {
          fontWeight: 500,
          marginBottom: "4px",
          color: theme.palette.text.primary,
        },
        ".recharts-tooltip-item": {
          color: theme.palette.text.secondary,
        },
        ".recharts-wrapper *:focus": {
          outline: "none", // removes browser-default border when chart is focused on
        },
        // Suppress scrollbars on touch devices, where users scroll by swiping and
        // native scrollbars add visual noise. Covers WebKit, Firefox and legacy Edge.
        ...(isTouchDevice && {
          "*": {
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE / legacy Edge
          },
          "*::-webkit-scrollbar": {
            display: "none", // Chrome, Safari, other WebKit
          },
          // Opt-in override: add className="show-scrollbar" to re-enable a thin
          // scrollbar on a specific scroll container (e.g. clamped descriptions
          // where the bar is a useful "more to read" affordance). The class
          // out-specifies the universal reset above, so no !important is needed.
          ".show-scrollbar": {
            scrollbarWidth: "thin", // Firefox
            msOverflowStyle: "auto", // IE / legacy Edge
          },
          ".show-scrollbar::-webkit-scrollbar": {
            display: "block", // Chrome, Safari, other WebKit
            width: "6px",
          },
          ".show-scrollbar::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.text.disabled,
            borderRadius: "3px",
          },
        }),
      }}
    />
  );

  return (
    <ThemeProvider theme={theme}>
      {inputGlobalStyles}
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <RouteErrorBoundary>
              <AppRoutes />
            </RouteErrorBoundary>
          </BrowserRouter>
          {process.env.NODE_ENV === "development" && <ReactQueryDevtools />}
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

// Remove any previously-registered (caching) service worker and its caches.
// The app no longer uses a service worker. See serviceWorkerRegistration.js.
serviceWorkerRegistration.unregister();

root.render(
  <React.StrictMode>
    <ThemeContextProvider>
      <Root />
    </ThemeContextProvider>
  </React.StrictMode>,
);
